import React, { useCallback, useMemo } from 'react';
import { Palette } from '@mui/material';
import StatusSelector, {
    StatusSelectorOptions,
} from 'components/SharedComponents/StatusSelector/StatusSelector';
import { TRACKABLE_EVENTS, trackEventPRH } from 'helpers/trackerHelpers';
import { t } from 'i18next';
import { CellRendererParams } from 'routes/BudgetModule/config/types';
import {
    PayComponentMapping,
    WageTypeSource,
} from 'routes/Mappings/components/PayComponentsTable/helpers/definitions';
import { useUpdatePayComponentDestination } from 'routes/Mappings/components/PayComponentsTable/hooks/payComponents';
import Flex from 'ui/Flex/Flex';

type CustomProps = {
    payComponentMapping: PayComponentMapping[];
    updatePayComponentSourcesMutation: () => void;
};
type Props = CellRendererParams & CustomProps;

export const PayComponentSelectorCellRenderer = ({
    value,
    data,
    payComponentMapping,
}: Props & { data: WageTypeSource & { payComponent: string } }) => {
    const selectedValue = value;
    const updatePayComponentDestinationsMutation =
        useUpdatePayComponentDestination();

    const handleChange = useCallback(
        (e: StatusSelectorOptions<number | null>) => {
            const newValue = e.value as unknown as number;
            // if the user selects the default option and a mapping already exist, we remove the source from the destination
            if (newValue === -1 && data?.destinationId) {
                updatePayComponentDestinationsMutation.mutate({
                    sourceId: data?.sourceId,
                    destinationId: null,
                });
            } else {
                updatePayComponentDestinationsMutation.mutate({
                    destinationId: newValue,
                    sourceId: data.sourceId,
                });
            }
            trackEventPRH(
                TRACKABLE_EVENTS.human_resources.settings.selectPayComponent,
                {
                    wageType: data.code,
                    payComponent: payComponentMapping.find(
                        (item) => item.destinationId === newValue,
                    )?.value,
                },
            );
        },
        [data, payComponentMapping, updatePayComponentDestinationsMutation],
    );
    const options = useMemo(
        () => [
            {
                label: data ? t('unmapped') : '',
                value: -1,
                color: 'primary' as keyof Palette,
            },
            ...payComponentMapping.map(
                (item): StatusSelectorOptions<number> => ({
                    label: item.value,
                    value: item.destinationId,
                    color: 'primary',
                }),
            ),
        ],
        [payComponentMapping],
    );

    return (
        <Flex>
            <StatusSelector<number | null>
                width="full-width"
                variant="outlined"
                tooltipValue={
                    selectedValue
                        ? options.find(
                              (choice) => choice.label === selectedValue,
                          )?.label
                        : t('unmapped')
                }
                options={options}
                canEdit={Boolean(data)}
                defaultOption={{
                    label: (selectedValue as string) ?? '',
                    value:
                        payComponentMapping.find(
                            (item) => item.value === selectedValue,
                        )?.destinationId ?? -1,
                    color: 'primary',
                }}
                onChange={(e) => handleChange(e)}
            />
        </Flex>
    );
};
